import * as z from "zod";

/** Names of all queryable datasets. */
export const DATASET_NAMES = [
  "quote_lines",
  "quotes",
  "items",
  "item_components",
  "item_operations",
  "operations",
  "companies",
  "people",
  "price_lists",
  "ship_to_addresses",
  "opportunities",
  "users",
  "demand_forecasts",
  "demand_forecast_lines",
  "locations",
] as const;

export const DatasetNameSchema = z.enum(DATASET_NAMES);
export type DatasetName = z.infer<typeof DatasetNameSchema>;

export const AggregateFunction = z.enum([
  "sum",
  "count",
  "count_distinct",
  "avg",
  "min",
  "max",
]);
export type AggregateFunction = z.infer<typeof AggregateFunction>;

export const FilterOperator = z.enum([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "not_in",
  "between",
  "is_null",
  "is_not_null",
]);
export type FilterOperator = z.infer<typeof FilterOperator>;

export const OrderDirection = z.enum(["asc", "desc"]);
export type OrderDirection = z.infer<typeof OrderDirection>;

export const DatasetSelectFieldSchema = z.object({
  field: z
    .string()
    .describe(
      "Field in table_prefix.column_name format. Root table fields use the dataset name as prefix (e.g. for dataset quote_lines, use quote_line.weight). Related table fields use their join prefix (e.g. rep.first_name, opportunity.status).",
    ),
  aggregate: AggregateFunction.optional().describe(
    "Aggregate function to apply. When any selected field uses an aggregate, all non-aggregated selected fields must appear in groupBy.",
  ),
  alias: z
    .string()
    .optional()
    .describe(
      "Alias for aggregated fields. Required to reference aggregated fields in orderBy.",
    ),
});
export type DatasetSelectField = z.infer<typeof DatasetSelectFieldSchema>;

export const DatasetFilterSchema = z.object({
  field: z
    .string()
    .describe("Field in table_prefix.column_name format (e.g. rep.first_name, quote_line.weight)."),
  op: FilterOperator.describe(
    "Filter operator. For 'between', pass a two-element array as value. For 'in'/'not_in', pass an array. For 'is_null'/'is_not_null', omit value.",
  ),
  value: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
      z.array(z.union([z.string(), z.number()])),
    ])
    .optional()
    .describe(
      "Filter value. Omit for is_null/is_not_null. Use a two-element array for between, an array for in/not_in. For date/timestamp fields, use ISO 8601 format (e.g. 2026-05-01T00:00:00Z).",
    ),
});
export type DatasetFilter = z.infer<typeof DatasetFilterSchema>;

export const DatasetOrderBySchema = z.object({
  field: z
    .string()
    .describe("Field in table_prefix.column_name format, or an alias defined on an aggregated select field."),
  direction: OrderDirection.default("asc"),
});
export type DatasetOrderBy = z.infer<typeof DatasetOrderBySchema>;

export const DatasetQuerySchema = z.object({
  dataset: DatasetNameSchema.describe(
    "Root dataset to query. Use get_dataset_schema to discover available fields and joins for each dataset.",
  ),
  select: z
    .array(DatasetSelectFieldSchema)
    .min(1)
    .describe("Fields to include in the results."),
  filters: z
    .array(DatasetFilterSchema)
    .optional()
    .describe("WHERE conditions to narrow results. Use filters to refine large datasets rather than relying on limit alone."),
  groupBy: z
    .array(z.string())
    .optional()
    .describe(
      "Fields to group by. Required when any selected field uses an aggregate — include all non-aggregated selected fields here.",
    ),
  orderBy: z
    .array(DatasetOrderBySchema)
    .optional()
    .describe("Result ordering. Reference aggregated fields by their alias."),
  limit: z
    .number()
    .int()
    .min(1)
    .max(1000)
    .optional()
    .default(100)
    .describe("Max rows to return (capped at 1000, default 100). Response includes rowCount and truncated flag when results exceed the limit."),
});
export type DatasetQuery = z.infer<typeof DatasetQuerySchema>;

export const DatasetResultSchema = z.object({
  columns: z.array(z.string()),
  csv: z.string().describe("CSV-formatted result data with header row followed by data rows."),
  rowCount: z.number(),
  truncated: z.boolean(),
});
export type DatasetResult = z.infer<typeof DatasetResultSchema>;

/** Schema field metadata returned by get_dataset_schema. */
export const DatasetFieldInfoSchema = z.object({
  field: z.string().describe("Field reference in prefix.column format"),
  type: z.string().describe("Column type (varchar, text, decimal, etc.)"),
  filterable: z.boolean(),
  aggregatable: z.boolean(),
  description: z
    .string()
    .optional()
    .describe("What the field contains, which currency/unit applies, and when it is null."),
  enumValues: z
    .array(z.string())
    .optional()
    .describe("Available values for enum fields (e.g. item.type → MANUFACTURED, PURCHASED, MATERIAL)"),
});
export type DatasetFieldInfo = z.infer<typeof DatasetFieldInfoSchema>;

export const DatasetJoinInfoSchema = z.object({
  prefix: z.string(),
  description: z.string(),
});
export type DatasetJoinInfo = z.infer<typeof DatasetJoinInfoSchema>;

export const DatasetSchemaInfoSchema = z.object({
  dataset: DatasetNameSchema,
  rootPrefix: z.string(),
  fields: z.array(DatasetFieldInfoSchema),
  joins: z.array(DatasetJoinInfoSchema),
});
export type DatasetSchemaInfo = z.infer<typeof DatasetSchemaInfoSchema>;

/** Request schema for AI-powered query generation. */
export const DatasetAIQueryRequestSchema = z.object({
  question: z.string().min(1).max(1000),
});
export type DatasetAIQueryRequest = z.infer<typeof DatasetAIQueryRequestSchema>;

/** Response schema for AI-powered query generation. */
export const DatasetAIQueryResponseSchema = z.object({
  query: DatasetQuerySchema,
  explanation: z.string(),
});
export type DatasetAIQueryResponse = z.infer<
  typeof DatasetAIQueryResponseSchema
>;
